import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { logger } from '@/lib/logger';
import { configureDownloads, reattachDownloads, verifyDownloadEntitlements } from './downloadManager';
import { getIsOnline, subscribeOnline } from './network';
import { flushProgressQueue } from './progressQueue';
import { loadRegistry } from './registry';

/**
 * Mounts once in the authenticated app shell: loads the registry, reconnects
 * background tasks, and on every return to connectivity replays queued
 * progress and re-verifies entitlements.
 */
export function useOfflineBootstrap(userId: string | null | undefined): void {
  const bootedRef = useRef(false);

  useEffect(() => {
    if (Platform.OS === 'web' || bootedRef.current) return;
    bootedRef.current = true;
    (async () => {
      try {
        configureDownloads();
        await loadRegistry();
        await reattachDownloads();
      } catch (err) {
        logger.error('[offline] bootstrap failed', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web' || !userId) return;
    let cancelled = false;

    const onOnline = async () => {
      if (cancelled) return;
      try {
        await loadRegistry();
        await flushProgressQueue();
        await verifyDownloadEntitlements(userId);
      } catch (err) {
        logger.debug('[offline] online sync failed', err);
      }
    };

    void getIsOnline().then((online) => {
      if (online) void onOnline();
    });
    const unsubscribe = subscribeOnline((online) => {
      if (online) void onOnline();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);
}
